import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  collection, onSnapshot, query, where, documentId,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { subscribeLeads } from '../../data/listeners/leads';
import { useConfig } from '../../context/ConfigContext';
import { polygonCentroid } from '../../utils/mapGeometry';
import {
  HUB, STAGES, STAGE_COLOR, CAT_COLOR,
} from '../../data/leadConstants';

// Decode the stored {lat,lng} polygon/centroid into Leaflet [lat,lng] tuples.
function decodeTerritory(d) {
  const t = d.data();
  return {
    id: d.id,
    label: t.label ?? d.id,
    color: t.color ?? '#7b3fb8',
    active: t.active ?? true,
    polygon: Array.isArray(t.polygon) ? t.polygon.map((p) => [p.lat, p.lng]) : [],
  };
}

// Territory Map = a READ-ONLY view of the territories defined in Territory
// Settings (the single source of truth for shapes/names) + lead pins + a hub
// coverage ring. The radius slider ONLY moves the ring; it does not change any
// territory (radius and quadrants are decoupled — DoR pending).
export default function TerritoryMap() {
  const { tenantId, role, territoryIds } = useAuth();
  const { openLead } = useUI();
  const { config } = useConfig();
  const channels = config.channels ?? [];
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({});
  const openRef = useRef(openLead);
  openRef.current = openLead;
  const [leads, setLeads] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [radiusKm, setRadiusKm] = useState(3);
  const [colorBy, setColorBy] = useState('status'); // 'status' | 'category'
  const [showFilters, setShowFilters] = useState(true);
  const [stageSet, setStageSet] = useState(() => new Set(STAGES));
  const [catSet, setCatSet] = useState(() => new Set(config.channels ?? []));

  useEffect(() => subscribeLeads({ tenantId, role, territoryIds }, setLeads), [tenantId, role, territoryIds]);

  // Live territories, scoped like the rules: agents/managers read only their own
  // territory docs (an unscoped list would be denied doc-by-doc).
  useEffect(() => {
    if (!tenantId) { setTerritories([]); return undefined; }
    let q;
    if (role === 'agent' || role === 'manager') {
      const ids = (territoryIds || []).slice(0, 30);
      if (ids.length === 0) { setTerritories([]); return undefined; }
      q = query(collection(db, 'territories'), where(documentId(), 'in', ids));
    } else {
      q = query(collection(db, 'territories'), where('tenantId', '==', tenantId));
    }
    return onSnapshot(q, (snap) => setTerritories(snap.docs.map(decodeTerritory)), () => setTerritories([]));
  }, [tenantId, role, territoryIds]);

  // This tenant's hub coordinates (fallback to the default) so the map centers
  // and draws its coverage ring on the RIGHT city, per tenant.
  const [hub, setHub] = useState(HUB);
  useEffect(() => {
    if (!tenantId) return undefined;
    const qs = query(collection(db, 'stores'), where('tenantId', '==', tenantId));
    return onSnapshot(qs, (snap) => {
      const s = snap.docs[0]?.data();
      if (s && Number.isFinite(Number(s.lat)) && Number.isFinite(Number(s.lng))) setHub([Number(s.lat), Number(s.lng)]);
      else setHub(HUB);
    }, () => setHub(HUB));
  }, [tenantId]);

  // Recenter + move the hub ring/marker when the hub resolves.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setView(hub, map.getZoom());
    if (layersRef.current.circle) layersRef.current.circle.setLatLng(hub);
    if (layersRef.current.hubMarker) layersRef.current.hubMarker.setLatLng(hub);
  }, [hub]);

  const scopedIds = useMemo(() => territories.map((t) => t.id), [territories]);

  const visibleLeads = useMemo(() => leads.filter((l) => (colorBy === 'status' ? stageSet.has(l.stage) : catSet.has(l.category))), [leads, colorBy, stageSet, catSet]);

  const perZone = useMemo(() => {
    const m = {};
    scopedIds.forEach((id) => { m[id] = 0; });
    visibleLeads.forEach((l) => { if (m[l.territoryId] != null) m[l.territoryId] += 1; });
    return m;
  }, [visibleLeads, scopedIds]);

  // Init map once.
  useEffect(() => {
    if (mapRef.current || !elRef.current) return undefined;
    const map = L.map(elRef.current).setView(HUB, 14);
    mapRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' }).addTo(map);
    layersRef.current.poly = L.layerGroup().addTo(map);
    layersRef.current.pins = L.layerGroup().addTo(map);
    layersRef.current.circle = L.circle(HUB, { radius: radiusKm * 1000, color: '#2E7D32', weight: 2, fillColor: '#2E7D32', fillOpacity: 0.05 }).addTo(map);
    layersRef.current.hubMarker = L.marker(HUB, { icon: L.divIcon({ className: '', iconSize: [30, 30], html: '<div style="background:#222227;color:#F3BC3E;border:2px solid #F3BC3E;border-radius:50%;width:30px;height:30px;display:grid;place-items:center;font-weight:700">H</div>' }) }).addTo(map).bindPopup('Hub');
    setTimeout(() => map.invalidateSize(), 60);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Hub coverage ring follows the slider ONLY (decoupled from territories).
  useEffect(() => {
    const { circle } = layersRef.current;
    if (circle) circle.setRadius(radiusKm * 1000);
  }, [radiusKm]);

  // Draw the SAVED territory polygons (read-only) + lead pins on changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const { poly, pins } = layersRef.current;
    poly.clearLayers(); pins.clearLayers();
    territories.forEach((t) => {
      if (t.polygon.length < 3) return;
      L.polygon(t.polygon, { color: t.color, weight: 3, fillColor: t.color, fillOpacity: t.active ? 0.14 : 0.04, dashArray: t.active ? null : '5,5' }).addTo(poly);
      L.marker(polygonCentroid(t.polygon), { interactive: false, icon: L.divIcon({ className: '', iconSize: [0, 0], html: `<div style="transform:translate(-50%,-50%);background:#fff;border:1.5px solid ${t.color};color:${t.color};font-weight:700;font-size:11px;padding:2px 9px;border-radius:11px;white-space:nowrap;box-shadow:0 1px 4px rgba(34,34,39,.25)">${t.label}</div>` }) }).addTo(poly);
    });
    visibleLeads.forEach((l) => {
      if (typeof l.lat !== 'number' || typeof l.lng !== 'number') return;
      const col = colorBy === 'status' ? STAGE_COLOR[l.stage] : CAT_COLOR[l.category];
      const marker = L.circleMarker([l.lat, l.lng], { radius: 8, color: '#fff', weight: 2, fillColor: col || '#9b9ba3', fillOpacity: 1 }).addTo(pins);
      marker.bindPopup(`<b>${l.businessName}</b><br>${l.stage} · ${l.category}<br><a href="#" data-lead="${l.id}">Open lead →</a>`);
      marker.on('popupopen', (e) => {
        const a = e.popup.getElement()?.querySelector('a[data-lead]');
        if (a) a.onclick = (ev) => { ev.preventDefault(); openRef.current?.(l.id); };
      });
    });
  }, [visibleLeads, territories, colorBy]);

  const flip = (set, setter, v) => { const n = new Set(set); if (n.has(v)) n.delete(v); else n.add(v); setter(n); };
  const items = colorBy === 'status' ? STAGES : channels;
  const colors = colorBy === 'status' ? STAGE_COLOR : CAT_COLOR;
  const activeSet = colorBy === 'status' ? stageSet : catSet;
  const setActive = colorBy === 'status' ? setStageSet : setCatSet;

  return (
    <div className="map-grid">
      <div id="map" ref={elRef} />
      <div className="map-side">
        <div className="maprow" style={{ justifyContent: 'space-between' }}>
          <b>Show filters</b>
          <label className="switch"><input type="checkbox" checked={showFilters} onChange={(e) => setShowFilters(e.target.checked)} /><span className="track" /></label>
        </div>
        {showFilters && (
          <>
            <div className="eyebrow" style={{ margin: '12px 0 6px' }}>Colour pins by</div>
            <div className="seg" style={{ width: '100%' }}>
              <button className={colorBy === 'status' ? 'on' : ''} style={{ flex: 1 }} onClick={() => setColorBy('status')}>Lead status</button>
              <button className={colorBy === 'category' ? 'on' : ''} style={{ flex: 1 }} onClick={() => setColorBy('category')}>Channel</button>
            </div>
            <div className="eyebrow" style={{ margin: '12px 0 6px' }}>{colorBy === 'status' ? 'Lead status' : 'Channel'}</div>
            {items.map((it) => (
              <label key={it} className="check">
                <input type="checkbox" checked={activeSet.has(it)} onChange={() => flip(activeSet, setActive, it)} />
                <span className="dotonly" style={{ background: colors[it] }} />{it}
              </label>
            ))}
          </>
        )}
        <div className="eyebrow" style={{ margin: '14px 0 8px' }}>Leads per zone</div>
        {territories.length === 0 && <div className="muted" style={{ fontSize: 'var(--text-caption)' }}>No territories defined yet — add them in Territory Settings.</div>}
        {territories.map((t) => (
          <div key={t.id} className="zone-card">
            <span className="sw" style={{ background: t.color }} /><span className="nm">{t.label}</span><span className="ct">{perZone[t.id] ?? 0}</span>
          </div>
        ))}
        <div className="slider">
          <div className="eyebrow" style={{ margin: '14px 0 0' }}>Hub coverage — <b>{radiusKm.toFixed(1)} km</b></div>
          <input type="range" min="1" max="5" step="0.5" value={radiusKm} onChange={(e) => setRadiusKm(Number(e.target.value))} />
          <p className="muted" style={{ fontSize: 'var(--text-caption)', marginTop: 4 }}>Display only — shows the hub&apos;s delivery radius. Territory shapes are managed in Territory Settings.</p>
        </div>
      </div>
    </div>
  );
}
